<template>
  <TemplateBase :page="page">
    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" />
    </template>

    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="true" />
    </template>

    <div class="grid grid-cols-12 relative v-gradient-bg v-gradient-bg--gold z-1 v-gradient-bg--gradient-only">
      <!--  Lyre-->
      <div
        v-if="page.youtube && page.youtube.url && !page.shortsEmbed"
        class="absolute top-0 bottom-0 left-0 right-0 opacity-40 v-gradient-bg z-[1] v-gradient-bg--gold v-gradient-bg--lyre-only v-gradient-bg--lyre-only--half-width"
      ></div>

      <!-- Video overlay-->
      <div
        v-if="overlayIsOpen && page.youtube && page.youtube.url && !page.shortsEmbed"
        class="absolute top-0 bottom-0 left-0 right-0 bg-[#cf7f009e] z-20 flex justify-center pt-28"
        @click="toggleOverlay()"
      >
        <div class="container z-grid v-page-section-container-padding">
          <div class="col-start-1 col-span-12 sm:col-start-2 sm:col-span-10">
            <div class="relative contains-iframe contains-youtube">
              <div ref="youtubeTarget"></div>
            </div>
          </div>
          <div class="col-start-12 col-span-1 hidden sm:block">
            <AtomIconX class="text-white w-5 h-5 sm:w-6 sm:h-6 ml-5 cursor-pointer" @click="toggleOverlay()" />
          </div>
        </div>
      </div>

      <!--  Video-header-->
      <div v-if="page.youtube && page.youtube.url && page.image && !page.shortsEmbed" class="col-span-full relative">
        <section class="relative sm:hidden col-span-full row-start-1 bg-transparent">
          <MoleculeMaskVisual :image="page.image" :shape-number="7" />
          <AtomIconPlay
            class="absolute top-0 z-10 left-2/4 top-[45%] translate-x-[-50%] sm:hidden cursor-pointer text-white w-14 h-14 xl:w-28 xl:h-28 mb-6"
            @click="toggleOverlay()"
          />
        </section>
        <section class="hidden sm:block absolute top-0 w-full bg-transparent">
          <MoleculeMaskVisual v-if="page.youtube && page.youtube.url" :image="page.image" :shape-number="8" />
        </section>

        <div class="hidden sm:block md:mb-6 relative z-10 mt-20 lg:mt-72">
          <div class="container z-grid v-page-section-container-padding-wide-screens">
            <div class="z-grid__cols flex items-center flex-col">
              <AtomIconPlay class="cursor-pointer text-white w-14 h-14 xl:w-28 xl:h-28 mb-6" @click="toggleOverlay()" />
              <h1 class="text-center text-2xl md:text-4xl xl:text-5xl text-white font-medium leading-tight">{{ page.title }}</h1>
              <h2 class="text-lg xl:text-2xl text-white font-normal leading-tight">{{ page.subtitle }}</h2>
              <span class="text-lg xl:text-2xl text-white font-normal leading-tight" :class="{ 'pt-1': page.subtitle }">
                <span v-if="!page.hideDate">{{ d_(page.postDate, 'long-date') }}</span>
                <span v-if="page.readingTime"><template v-if="!page.hideDate"> - </template>{{ page.readingTime }}</span>
                <span v-if="page.photoCredit"
                  ><template v-if="!page.hideDate || page.readingTime"> - </template>{{ $t('discovery-detail.photo-credit', { name: page.photoCredit }) }}</span
                >
              </span>
            </div>
          </div>
        </div>
      </div>

      <!--  Image-header-->
      <div v-else-if="page.image && !page.shortsEmbed" class="col-span-full row-start-1">
        <section class="relative sm:hidden bg-transparent z-0">
          <MoleculeMaskVisual :image="page.image" :shape-number="7" />
        </section>
        <section class="hidden sm:block absolute w-full bg-transparent z-0">
          <MoleculeMaskVisual :image="page.image" :shape-number="9" />
        </section>
      </div>

      <!--  Shorts-style header   -->
      <div v-if="page.shortsEmbed" class="mt-10 md:mt-14 mb-10 v-page-section-container-padding-wide-screens col-start-1 md:col-start-2 lg:col-start-3 col-span-full">
        <AtomHeadingDetailPageMain v-if="page.title" class="shorts-title">{{ page.title }}</AtomHeadingDetailPageMain>
      </div>

      <div v-if="page.shortsEmbed" class="flex col-span-full row-start-2 justify-center items-center">
        <div class="flex items-center gap-4 flex-nowrap overflow-hidden justify-center relative">
          <div v-for="(uri, index) of page.discoveries.slice(0, page.discoveries.length / 2)" :key="index">
            <OrganismLoaderFromUri v-slot="{ teaserImage, url }" :uri="uri" class="flex justify-center items-center">
              <MoleculeButtonShort v-if="index === page.discoveries.length / 2 - 1" class="absolute z-20 p-2 cursor-pointer" :url="url"> </MoleculeButtonShort>

              <MoleculeDiscoveryShort :image="teaserImage" :url="url"> </MoleculeDiscoveryShort>
            </OrganismLoaderFromUri>
          </div>

          <MoleculeDiscoveryShort :embed-code="page.shortsEmbed"></MoleculeDiscoveryShort>

          <div v-for="(uri, index) of page.discoveries.slice(page.discoveries.length / 2, page.discoveries.length)" :key="index">
            <OrganismLoaderFromUri v-slot="{ teaserImage, url }" :uri="uri" class="flex justify-center items-center">
              <MoleculeButtonShort v-if="index === 0" class="absolute z-20 p-2 cursor-pointer" :url="url" :next="true"> </MoleculeButtonShort>
              <MoleculeDiscoveryShort :image="teaserImage" :url="url"> </MoleculeDiscoveryShort>
            </OrganismLoaderFromUri>
          </div>
        </div>
      </div>

      <OrganismPageSectionContent
        class="row-start-3 col-span-full mb-8 md:mb-12 pt-5"
        :class="[(page.youtube && page.youtube.url) || page.shortsEmbed ? '' : 'mt-4 sm:mt-[30vw] lg:mt-[28vw] 2xl:mt-[32vw]']"
        :at-page-top="!page.image"
        background-color="bg-transparent sm:bg-white"
        :title="page.shortsEmbed ? '' : page.title"
        :subtitle="page.subtitle"
        :show-titles-on-large-screens="page.youtube && page.youtube.url ? false : true"
        :introduction="page.introduction"
        :description="page.description"
        :icon-left="page.isPreludiumContent ? 'app-preludium' : undefined"
      >
        <template #chapeau>
          <p class="text-xs sm:text-sm mb-1">
            <template v-if="!page.hideDate">{{ d_(page.postDate, 'long-date') }}</template>
            <template v-if="page.readingTime"><template v-if="!page.hideDate"> - </template>{{ page.readingTime }}</template>
            <template v-if="page.writer"
              ><template v-if="!page.hideDate || page.readingTime"> - </template>{{ $t('discoverdetail-writer-name', { name: page.writer.name }) }}</template
            >
            <template v-if="page.photoCredit"
              ><template v-if="!page.hideDate || page.readingTime || page.writer"> - </template>{{ $t('discovery-detail.photo-credit', { name: page.photoCredit }) }}</template
            >
          </p>
        </template>
        <section v-if="page.discoveryCollections.length" class="mt-8 py-6 border-t-2 border-b-2 border-solid border-app-primary-grey--black-squeeze">
          <h3 class="text-app-primary-1-red--torch md:text-app-primary-1-red--fire-brick md:text-2xl font-semibold mb-2 md:mb-3">{{ $t('discoverdetail-collections-title') }}</h3>

          <ul class="flex flex-wrap">
            <li v-for="(collection, index) of page.discoveryCollections" :key="index" class="mr-1 mb-1 md:mr-3 md:mb-3">
              <MoleculeButton :url="collection.url" size="medium" class="md:hidden">
                {{ collection.title }}
              </MoleculeButton>
              <MoleculeButton :url="collection.url" class="hidden md:flex">
                {{ collection.title }}
              </MoleculeButton>
            </li>
          </ul>
        </section>
      </OrganismPageSectionContent>

      <MoleculeMatrix class="row-start-4 col-span-full z-[1]" :items="page.dynamicContent" background-color="bg-transparent sm:bg-white" :color-theme="'gold'" />

      <section v-if="page.discoveries.length && !page.shortsEmbed" class="row-start-5 col-span-full container z-grid v-page-section-container-padding mt-6 mb-16 z-[1]">
        <h2 class="col-span-full lg:col-span-10 lg:col-start-2 text-app-primary-1-red--fire-brick text-xl sm:text-3xl font-semibold mb-4 md:mb-6">
          {{ $t('discoverdetail-discovermore-title') }}
        </h2>
        <ul class="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-8">
          <li v-for="(uri, index) of page.discoveries" :key="index" class="flex justify-center" :class="{ 'lg:hidden 2xl:flex': index === 3 }">
            <OrganismLoaderFromUri v-slot="{ title, discoveryCollections, teaserImage, url, category }" :uri="uri">
              <MoleculeCardOverview
                :title="title"
                :subtitle="get(discoveryCollections, '[0].title')"
                :image="teaserImage"
                :url="url"
                :icon="get(category, 'icon.slug')"
                background-color="v-gradient-bg  v-gradient-bg--gold v-gradient-bg--gradient-only"
                type="discover"
              />
            </OrganismLoaderFromUri>
          </li>
        </ul>
      </section>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { usePlayer } from '@vue-youtube/core'

const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const { d_ } = setupTranslationPrefixer('DiscoveryDetail')

const overlayIsOpen = ref(false)
const toggleOverlay = () => {
  overlayIsOpen.value = !overlayIsOpen.value
}

// `as const` keeps the 0/1 literals narrow enough for the player's own option types.
const playerVars = {
  autoplay: 1,
  rel: 0,
  modestbranding: 1,
} as const

// The target only exists while the overlay is open (v-if); usePlayer registers the player once it mounts.
const youtubeTarget = ref()
const videoId = computed(() => getYoutubeIdFromUrl(props.page.youtube?.url) ?? '')
usePlayer(videoId, youtubeTarget, { playerVars })
</script>

<style scoped>
.shorts-title {
  margin-left: 3rem;
  margin-top: 2rem;
}
</style>
